import type { Tour } from '../tour-types';

/**
 * The pipelines index. The empty index is marked as a first-run state, so the tour waits
 * until a pipeline exists. An open board (`?pipeline=`) shares the route and is reported as the `board`
 * screen, with a tour of its own below.
 */
export const pipelinesTour: Tour = {
  key: 'pipelines',
  version: 1,
  routes: ['/experiments/pipelines'],
  steps: [
    {
      anchor: 'pipelines-new',
      placement: 'bottom',
      requires: 'pipelines:manage',
      title: 'New pipeline',
      body: 'Name a pipeline to plan a run of experiments in stages. It starts as a dry run, so nothing is started until you arm it.',
    },
    {
      anchor: 'pipelines-list',
      placement: 'top',
      title: 'Your pipelines',
      body: 'Each card shows its status, whether automation is on, and how far through its stages it is. Open one to edit its board.',
    },
  ],
};

/**
 * One pipeline's board, open on the same route as the index. The collision banner only
 * shows when a stage holds a pair the engine would refuse to start; the stage steps point
 * at the first stage, and the card step at the first card in the first stage that has one.
 */
export const pipelineBoardTour: Tour = {
  key: 'pipelines.board',
  version: 1,
  routes: ['/experiments/pipelines'],
  screen: 'board',
  steps: [
    {
      anchor: 'pipelines-controls',
      placement: 'bottom',
      requires: 'pipelines:manage',
      title: 'Save and control',
      body: 'Changes to stages and cards stay on this page until you press Save board. While the pipeline runs you can pause it, end the current stage early, or cancel it, and ending or cancelling cannot be undone.',
    },
    {
      anchor: 'pipelines-collision',
      placement: 'bottom',
      title: 'Conflicting experiments',
      body: 'Two experiments in one stage would interfere, so the engine will not start that stage. The flagged stage names the pair: move one of them to a later stage.',
    },
    {
      anchor: 'pipelines-automation',
      placement: 'bottom',
      title: 'Pipeline settings',
      body: 'Run automatically lets the engine start and stop each stage for you, and Deploy the winner also ships the result. With both off, it only records what it would have done.',
    },
    {
      anchor: 'pipelines-drafts',
      requires: 'pipelines:manage',
      title: 'Unassigned drafts',
      body: 'Drag a draft onto a stage to add it, or back here to take it off. Only drafts can join a board.',
    },
    {
      anchor: 'pipelines-stage',
      title: 'Stages',
      body: 'Stages run one after another, and everything in a stage runs at the same time. Once a stage has started it is locked, so it can no longer be renamed, moved or given different experiments.',
    },
    {
      anchor: 'pipelines-advance-rule',
      title: 'When a stage ends',
      body: 'Choose whether the stage waits for every experiment to stop, ends at the first conclusive result, or waits for a person. With automation on, the next stage starts once this one ends.',
    },
    {
      anchor: 'pipelines-card',
      title: 'Experiments in a stage',
      body: 'Drag a card to another stage to reorder the plan. An amber or red outline means it clashes with another experiment in the same stage.',
    },
    {
      anchor: 'pipelines-add-stage',
      placement: 'left',
      requires: 'pipelines:manage',
      title: 'Add a stage',
      body: 'Add a stage for each round of testing. Put experiments that touch the same page in different stages.',
    },
  ],
};
